/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    TablePagination,
    Autocomplete,
    FormControl,
    Tooltip,
    CircularProgress,
    InputAdornment
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { Ftp } from '../types/api.types';
import { ftpService } from '../services/api/ftpService';
import { useApiState } from '../context/ApiStateContext';
import LoadingOverlay from '../components/LoadingOverlay';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusPopup from '../components/StatusPopup';

// Add FtpForm type for form state
type FtpForm = {
    protocol: string;
    host: string;
    port: number;
    user: string;
    password: string;
};

const FtpPage: React.FC = () => {
    const [ftps, setFtps] = useState<Ftp[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingFtp, setEditingFtp] = useState<Ftp | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const { loading, setLoading, setError, setSuccess } = useApiState();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [ftpToDelete, setFtpToDelete] = useState<number | null>(null);
    // Use FtpForm for formData
    const [formData, setFormData] = useState<FtpForm>({
        protocol: '',
        host: '',
        port: 0,
        user: '',
        password: ''
    });
    const isFirstRender = useRef(true);
    const [testResult, setTestResult] = useState<{ [key: number]: { success: boolean; message: string } }>({});
    const [statusPopup, setStatusPopup] = useState<{
        open: boolean;
        success: boolean;
        message: string;
    }>({
        open: false,
        success: false,
        message: ''
    });
    const [dialogTestResult, setDialogTestResult] = useState<null | { success: boolean; message: string }>(null);
    const [dialogTestLoading, setDialogTestLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const fetchFtps = async (currentPage: number = 0, currentPageSize: number = 10, search: string = '') => {
        setLoading(true);
        try {
            const response = await ftpService.getAllFtps(search.trim(), currentPage + 1, currentPageSize);
            setFtps(response.data);
            setTotal(response.total);
            setSuccess(true);
        } catch (error) {
            setError(error as any);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            fetchFtps(0, 10);
            isFirstRender.current = false;
            return;
        }

        if (page > 0 || rowsPerPage !== 10) {
            fetchFtps(page, rowsPerPage);
        }
    }, [page, rowsPerPage]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAdd = () => {
        setEditingFtp(null);
        setFormData({
            protocol: '',
            host: '',
            port: 0,
            user: '',
            password: ''
        });
        setDialogTestResult(null);
        setDialogTestLoading(false);
        setDialogOpen(true);
    };

    const handleEdit = async (ftp: Ftp) => {
        setLoading(true);
        try {
            const updatedFtp = await ftpService.getFtpById(ftp.id!);
            setEditingFtp(updatedFtp);
            setFormData({
                protocol: updatedFtp.protocol,
                host: updatedFtp.host,
                port: updatedFtp.port,
                user: updatedFtp.user,
                password: updatedFtp.password
            });
            setTestResult(prev => {
                const newResults = { ...prev };
                delete newResults[ftp.id!];
                return newResults;
            });
            setDialogTestResult(null);
            setDialogTestLoading(false);
            setDialogOpen(true);
        } catch (error) {
            setError(error as any);
        }
        setLoading(false);
    };

    const handleDeleteClick = (ftp: number) => {
        setFtpToDelete(ftp);
        setDeleteDialogOpen(true);
    };

    const showStatusPopup = (success: boolean, message: string) => {
        setStatusPopup({
            open: true,
            success,
            message
        });
    };

    const handleStatusPopupClose = () => {
        setStatusPopup(prev => ({ ...prev, open: false }));
    };

    const handleDeleteConfirm = async () => {
        if (!ftpToDelete) return;

        setLoading(true);
        try {
            await ftpService.deleteFtp(ftpToDelete!);
            showStatusPopup(true, 'FTP deleted successfully!');
            fetchFtps(page, rowsPerPage);
        } catch (error) {
            showStatusPopup(false, (error as any)?.message || 'Delete operation failed');
        }
        setLoading(false);
        setDeleteDialogOpen(false);
        setFtpToDelete(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setFtpToDelete(null);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        const formDataObj = new FormData(event.currentTarget);
        const values = {
            host: formDataObj.get('host') as string,
            port: parseInt(formDataObj.get('port') as string),
            user: formDataObj.get('user') as string,
            password: formDataObj.get('password') as string,
            protocol: formDataObj.get('protocol') as string || formData.protocol,
        };

        try {
            if (editingFtp) {
                await ftpService.updateFtp(editingFtp.id!, values);
                showStatusPopup(true, 'FTP updated successfully!');
            } else {
                await ftpService.createFtp(values);
                showStatusPopup(true, 'FTP created successfully!');
            }
            setDialogOpen(false);
            fetchFtps(page, rowsPerPage);
        } catch (error) {
            showStatusPopup(false, (error as any)?.message || 'Operation failed');
        }
        setLoading(false);
    };

    const handleSearch = () => {
        if (!searchQuery.trim()) {
            // If search is empty, fetch all FTPS
            fetchFtps(page, rowsPerPage);
            return;
        }
        setPage(0); // Reset to first page when searching
        fetchFtps(0, rowsPerPage, searchQuery);
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleTestConnection = async (ftp: Ftp) => {
        setLoading(true);
        try {
            const result = await ftpService.testConnection({
                protocol: ftp.protocol,
                host: ftp.host,
                port: ftp.port,
                user: ftp.user,
                password: ftp.password
            });

            setTestResult(prev => ({
                ...prev,
                [ftp.id!]: {
                    success: result.success,
                    message: result.message || result.error || 'Connection test failed'
                }
            }));

            if (result.success) {
                showStatusPopup(true, 'Connection test successful!');
            } else {
                showStatusPopup(false, result.message || result.error || 'Connection test failed');
            }
        } catch (error) {
            setTestResult(prev => ({
                ...prev,
                [ftp.id!]: {
                    success: false,
                    message: (error as any)?.message || 'Connection test failed'
                }
            }));
            showStatusPopup(false, (error as any)?.message || 'Connection test failed');
        }
        setLoading(false);
    };

    const handleDialogTestConnection = async () => {
        setDialogTestLoading(true);
        setDialogTestResult(null);
        try {
            const result = await ftpService.testConnection({
                protocol: formData.protocol,
                host: formData.host,
                port: formData.port,
                user: formData.user,
                password: formData.password
            });
            setDialogTestResult({
                success: result.success,
                message: result.message || result.error || 'Connection test failed'
            });
            showStatusPopup(result.success, result.message || result.error || 'Connection test failed');
        } catch (error) {
            setDialogTestResult({
                success: false,
                message: (error as any)?.message || 'Connection test failed'
            });
            showStatusPopup(false, (error as any)?.message || 'Connection test failed');
        }
        setDialogTestLoading(false);
    };

    const handleDialogClose = (event?: any, reason?: string) => {
        if (dialogTestLoading) return;
        setDialogOpen(false);
        setDialogTestResult(null);
        setDialogTestLoading(false);
    };

    return (
        <Box sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: { xs: 2, sm: 3 },
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minHeight: 'calc(100vh - 120px)'
        }}>
            <Box sx={{ p: 0 }}>
                <LoadingOverlay open={loading} />

                {/* Status Popup */}
                <StatusPopup
                    open={statusPopup.open}
                    success={statusPopup.success}
                    message={statusPopup.message}
                    onClose={handleStatusPopupClose}
                />

                {/* Header Section */}
                <Box sx={{
                    mb: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            FTP Management
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Configure and manage your FTP connections securely
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAdd}
                            sx={{
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                            }}
                        >
                            Add FTP
                        </Button>
                    </Box>
                </Box>

                {/* Search Section */}
                <Box sx={{
                    mb: 3,
                    p: 3,
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    <TextField
                        size="small"
                        placeholder="Search FTPs..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                        sx={{
                            minWidth: 300,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                            }
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        disabled={loading}
                        sx={{ borderRadius: 2 }}
                    >
                        Search
                    </Button>
                </Box>

                {/* Table Section */}
                <Box sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                    <TableCell sx={{ fontWeight: 600 }}>Host</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Port</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Password</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Protocol</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {ftps.map((ftp) => (
                                    <TableRow
                                        key={ftp.id}
                                        sx={{
                                            '&:hover': { bgcolor: '#f8f9fa' },
                                            '&:nth-of-type(even)': { bgcolor: '#fafafa' }
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: 500 }}>{ftp.host}</TableCell>
                                        <TableCell>{ftp.port}</TableCell>
                                        <TableCell>{ftp.user}</TableCell>
                                        <TableCell>{ftp.password ? '•'.repeat(ftp.password.length) : ''}</TableCell>
                                        <TableCell>
                                            <Box sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 1,
                                                bgcolor: ftp.protocol === 'SFTP' ? 'rgba(118, 7, 49, 0.1)' : '#f3e5f5',
                                                color: ftp.protocol === 'SFTP' ? '#760731' : '#7b1fa2',
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                            }}>
                                                {ftp.protocol}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                <Tooltip title={testResult[ftp.id!]?.message || 'Test Connection'}>
                                                    <IconButton
                                                        onClick={() => handleTestConnection(ftp)}
                                                        color={testResult[ftp.id!]?.success ? 'success' : testResult[ftp.id!] ? 'error' : 'primary'}
                                                        disabled={loading}
                                                        sx={{
                                                            mr: 1,
                                                            '&:hover': {
                                                                bgcolor: testResult[ftp.id!]?.success ? 'rgba(76, 175, 80, 0.1)' :
                                                                    testResult[ftp.id!] ? 'rgba(211, 47, 47, 0.1)' :
                                                                        'rgba(118, 7, 49, 0.1)'
                                                            }
                                                        }}
                                                    >
                                                        {testResult[ftp.id!]?.success ? <CheckCircleIcon /> : testResult[ftp.id!] ? <ErrorIcon /> : <ErrorIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                                <IconButton
                                                    onClick={() => handleEdit(ftp)}
                                                    color="primary"
                                                    sx={{
                                                        mr: 1,
                                                        '&:hover': { bgcolor: 'rgba(118, 7, 49, 0.1)' }
                                                    }}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleDeleteClick(ftp.id!)}
                                                    color="error"
                                                    sx={{
                                                        '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.1)' }
                                                    }}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            borderTop: '1px solid #e0e0e0',
                            bgcolor: '#f8f9fa'
                        }}
                    />
                </Box>

                <ConfirmDialog
                    open={deleteDialogOpen}
                    title="Delete FTP"
                    message={`Are you sure you want to delete FTP ${ftpToDelete?.toString()}?`}
                    onConfirm={handleDeleteConfirm}
                    onCancel={handleDeleteCancel}
                />

                {/* Add/Edit Dialog */}
                <Dialog
                    open={dialogOpen}
                    onClose={handleDialogClose}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 2 }
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        <DialogTitle sx={{
                            borderBottom: '1px solid #e0e0e0',
                            bgcolor: '#f8f9fa'
                        }}>
                            {editingFtp ? 'Edit FTP' : 'Add FTP'}
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <FormControl fullWidth>
                                    <Autocomplete
                                        options={['FTP', 'SFTP']}
                                        value={formData.protocol || editingFtp?.protocol || ''}
                                        onChange={(event, newValue) => {
                                            setFormData(prev => ({
                                                ...prev,
                                                protocol: newValue || '',
                                                port: newValue === 'FTP' ? 21 : newValue === 'SFTP' ? 22 : 0,
                                                host: '',
                                                user: '',
                                                password: ''
                                            }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Protocol"
                                                required
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        borderRadius: 1.5
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </FormControl>
                                <TextField
                                    autoFocus
                                    name="host"
                                    label="Host"
                                    type="text"
                                    fullWidth
                                    required
                                    value={formData.host}
                                    onChange={(e) => setFormData(prev => ({ ...prev, host: e.target.value }))}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                                <TextField
                                    name="port"
                                    label="Port"
                                    type="number"
                                    fullWidth
                                    required
                                    value={formData.port}
                                    onChange={(e) => setFormData(prev => ({ ...prev, port: parseInt(e.target.value) || 0 }))}
                                    inputProps={{ min: 1, max: 65535 }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                                <TextField
                                    name="user"
                                    label="User"
                                    type="text"
                                    fullWidth
                                    required
                                    value={formData.user}
                                    onChange={(e) => setFormData(prev => ({ ...prev, user: e.target.value }))}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                />
                                <TextField
                                    name="password"
                                    label="Password"
                                    type={showPassword ? 'text' : 'password'}
                                    fullWidth
                                    required
                                    value={formData.password}
                                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 1.5
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                    onClick={() => setShowPassword((show) => !show)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{
                            p: 3,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    onClick={handleDialogTestConnection}
                                    disabled={dialogTestLoading || loading}
                                    variant="outlined"
                                    startIcon={dialogTestLoading ? <CircularProgress size={20} /> : null}
                                    sx={{ borderRadius: 2 }}
                                >
                                    Test Connection
                                </Button>
                                {dialogTestResult && (
                                    dialogTestResult.success ?
                                        <CheckCircleIcon color="success" sx={{ ml: 1 }} /> :
                                        <ErrorIcon color="error" sx={{ ml: 1 }} />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    onClick={handleDialogClose}
                                    disabled={dialogTestLoading || loading}
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading || dialogTestLoading}
                                    sx={{
                                        borderRadius: 2,
                                        boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
                                    }}
                                >
                                    {editingFtp ? 'Update' : 'Create'}
                                </Button>
                            </Box>
                        </DialogActions>
                    </form>
                </Dialog>
            </Box>
        </Box>
    );
};

export default FtpPage; 